/**
 * Pricing Configuration
 *
 * Contains pricing tiers, Stripe price IDs, and product metadata
 * for both Stand Designer and Visual Forester Pro.
 *
 * Last Updated: 2024-11-21
 */

export const PRICING = {
  'stand-designer': {
    name: 'Stand Designer',
    isFree: true,
    yearly: {
      tier1: { seats: 1, price: 0, savings: 0 },
      tier2: { seats: '2-9', price: 0, savings: 0 },
      tier3: { seats: '10+', price: 0, savings: 0 }
    },
    monthly: {
      tier1: { seats: 1, price: 0, savings: 0 },
      tier2: { seats: '2-9', price: 0, savings: 0 },
      tier3: { seats: '10+', price: 0, savings: 0 }
    }
  },
  'vf-pro': {
    name: 'Visual Forester Pro',
    isFree: true, // Beta period - free with code
    isBeta: true,
    betaCode: 'VFProBeta2026',
    yearly: {
      tier1: { seats: 1, price: 299, savings: 0 },
      tier2: { seats: '2-9', price: 269, savings: 30 },
      tier3: { seats: '10+', price: 249, savings: 50 }
    },
    monthly: {
      tier1: { seats: 1, price: 29, savings: 0 },
      tier2: { seats: '2-9', price: 26, savings: 3 },
      tier3: { seats: '10+', price: 24, savings: 5 }
    }
  }
}

/**
 * Get pricing for a specific product and interval
 * @param {string} productType - 'stand-designer' or 'vf-pro'
 * @param {string} interval - 'yearly' or 'monthly'
 * @param {number} quantity - Number of seats
 * @returns {object} { price, savings }
 */
export const getPricing = (productType, interval, quantity) => {
  const product = PRICING[productType]
  if (!product) {
    console.error(`Invalid product type: ${productType}`)
    return { price: 0, savings: 0 }
  }

  const intervalPricing = product[interval]
  if (!intervalPricing) {
    console.error(`Invalid interval: ${interval}`)
    return { price: 0, savings: 0 }
  }

  let tier
  if (quantity >= 10) {
    tier = intervalPricing.tier3
  } else if (quantity >= 2 && quantity <= 9) {
    tier = intervalPricing.tier2
  } else if (quantity === 1) {
    tier = intervalPricing.tier1
  } else {
    // Invalid quantity
    return { price: 0, savings: 0 }
  }

  return {
    price: tier.price,
    savings: tier.savings * quantity
  }
}

/**
 * Stripe Price IDs
 */
export const STRIPE_PRICE_IDS = {
  'stand-designer': {
    yearly: 'price_1JPF3VJL9BSphru31Qx9nReC',
    monthly: 'price_1JPF3qJL9BSphru3g37rEdd5'
  },
  'vf-pro': {
    yearly: 'price_1SW1BgJL9BSphru3ajgUbU8C', // Product: prod_TSx5DeovIJRNhk
    monthly: 'price_1SW1BgJL9BSphru3YEUqNsnT' // Product: prod_TSx5DeovIJRNhk
  }
}

/**
 * Product metadata
 */
export const PRODUCT_META = {
  'stand-designer': {
    isFree: true,
    hasSubscription: true, // Still create subscription record in Firebase for seat tracking
    requiresStripe: false
  },
  'vf-pro': {
    isFree: true, // Beta period
    isBeta: true,
    hasSubscription: true,
    requiresStripe: false
  }
}
